import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Opportunity, OpportunityPayload } from 'Event/interfaces'
import { FormModal } from 'components/FormModal'
import { pick } from 'lodash'
import { EventDateAndRoleScreen, EventDateScreenValues } from 'Event/components/EventDateAndRoleScreen'
import { EventInfoScreen, EventInfoScreenValues } from 'Event/components/EventInfoScreen'
import {
  EventRegistrationDetailsScreen,
  EventRegistrationDetailsScreenValues,
} from 'Event/components/EventRegistrationDetailsScreen'
import { useGroups } from 'group/hooks/useGroups'
import { Group } from 'Event/components/opportunity/GroupPicker/types'
import { useEnabledGroups } from 'Event/components/opportunity/GroupPicker/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import { useGetEventGroups } from 'Event/hooks/useGetEventGroups'
import { useIsTester } from 'auth/hooks'
import Loading from 'civic-champs-shared/core/Loading'
import {
  OpportunityTemplateCredential,
  OpportunityTemplatePersonGroup,
  OpportunityTemplateSurvey,
} from 'volunteering/opportunities/interfaces'
import useGetOpportunityTemplateGroups from 'volunteering/opportunities/hooks/useGetOpportunityTemplateGroups'
import { useRolesCollection } from 'volunteer-role/hooks'
import { Role } from 'volunteer-role/types'
import {
  EventPayload,
  FormData,
  getFormValuesFromOpportunityTemplate,
  mapFormDataToEventPayload,
  mapFormDataToOpportunityPayload,
  mapToEventGroup,
} from 'Event/helpers/add-edit-helpers'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { AssociationType, OpportunityResponseWithRecurrenceInfo } from 'Event/interfaces/interfaceCreateEditEvent'
import useGetEventQuestionSets from 'Event/hooks/useGetEventQuestionSets'
import { encodeOccurrenceFromEvent } from 'Event/helpers/encodeOccurrence'
import { useGetFormDataFromEvent } from '../hooks/useGetFormDataFromEvent'
import { useShowPrompt } from '../../civic-champs-shared/core/modal/hooks'
import EditRecurringEventConfirmDialog, { EditType } from './EditRecurringEventConfirmDialog'
import { RECURRING_TYPE } from '../../civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import useGetOpportunityTemplateSurveys from 'volunteering/opportunities/hooks/useGetOpportunityTemplateSurveys'
import useGetOpportunityTemplateCredentials from 'volunteering/opportunities/hooks/useGetOpportunityTemplateCredentials'
import { useLocationsCollection } from 'locations/hooks'
import moment from 'moment-timezone'

export const AddEditEventPrompt = ({
  showing,
  close,
  complete,
  event,
}: {
  showing: boolean
  complete: (value: OpportunityPayload | EventPayload) => void
  close: () => void
  event?: Opportunity | OpportunityResponseWithRecurrenceInfo
}) => {
  const getFormDataFromEvent = useGetFormDataFromEvent({ useTemplate: true })
  const showEditRecurringPrompt = useShowPrompt(EditRecurringEventConfirmDialog)
  const [isNewEvent, isDraftable] = useMemo(() => {
    if (!event) {
      return [true, true]
    } else {
      return [false, (event as OpportunityResponseWithRecurrenceInfo).schedulable && !event.published]
    }
  }, [event])
  const currentOrg = useCurrentOrg()
  const [screen, setScreen] = useState(0)
  const { groups: initialGroups, loading: groupsLoading } = useGroups() as { groups: Group[]; loading: boolean }
  const groups = useEnabledGroups(initialGroups)
  const [getEventGroups, { loading: eventGroupsLoading }] = useGetEventGroups()
  const [fetchOpportunityTemplateGroups] = useGetOpportunityTemplateGroups()
  const [fetchOpportunityTemplateSurveys] = useGetOpportunityTemplateSurveys()
  const [fetchOpportunityTemplateCredentials] = useGetOpportunityTemplateCredentials()
  const { loading: isTesterLoading, isTester: isTest } = useIsTester()
  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')
  const isSimplifyRecurringEventRulesEnabled = useFeatureEnabled('SimplifyRecurringEventRules')
  const [{ roles, initiallyLoaded: rolesLoaded }, { addRole }] = useRolesCollection({ reporting: false })
  const titles = useMemo(
    () => [(event ? 'Edit' : 'Add') + ' Event', 'Event Information', 'Registration Details'],
    [event],
  )

  const [{ locations, loading: locationsLoading, initiallyLoaded: locationsInitiallyLoaded }] = useLocationsCollection()
  const namedLocations = useMemo(() => {
    return locations.filter(({ name }) => name !== null)
  }, [locations])

  const [getQuestionSets, { result: questionSets, loading: questionSetsLoading }] = useGetEventQuestionSets()
  useEffect(() => {
    if (locationsInitiallyLoaded && event && (event as OpportunityResponseWithRecurrenceInfo).schedulable) {
      getQuestionSets(encodeOccurrenceFromEvent(event)).then(questionSets =>
        setFormData(getFormDataFromEvent({ event, questionSets, isQuestionnaireAsSurveyEnabled, namedLocations })),
      )
    }
  }, [locationsInitiallyLoaded, event]) // eslint-disable-line react-hooks/exhaustive-deps
  const initialFormData = useMemo(() => {
    return getFormDataFromEvent({ event, questionSets, isQuestionnaireAsSurveyEnabled, namedLocations })
  }, [event, getFormDataFromEvent, isQuestionnaireAsSurveyEnabled, namedLocations, questionSets])

  const [formData, setFormData] = useState<FormData>(initialFormData)

  useEffect(() => {
    event &&
      getEventGroups(event.id).then(eventGroups => {
        const groups = eventGroups.map(mapToEventGroup)
        setFormData(data => ({
          ...data,
          groups,
        }))
      })
  }, [event]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFormData(data => ({
      ...data,
      isTest,
    }))
  }, [isTest])

  const dateValues: EventDateScreenValues = useMemo(
    () => ({
      ...pick(formData, [
        'startDate',
        'endDate',
        'startTime',
        'endTime',
        'opportunityTemplate',
        'recurringType',
        'roles',
        'isSchedulable',
        'dates',
        'simpleRecurringType',
        'monthlyRecurringType',
      ]),
      useTemplate: isNewEvent,
    }),
    [formData, isNewEvent],
  )
  const eventInformationValues = useMemo(
    () =>
      ({
        name: formData.name,
        description: formData.description,
        address: formData.address || '',
        city: formData.city || '',
        state: formData.state || '',
        zip: formData.zip || '',
        visibility: formData.visibility,
        geofencing: formData.geofencing,
        visibilityGroups: formData.groups.filter(
          ({ associationType }) => associationType === AssociationType.EVENT_PRIVATE_TO_MEMBERS,
        ),
        locationIsAddress: formData.locationIsAddress,
        locationDetails: formData.locationDetails || '',
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
        contactPhoneNumber: formData.contactPhoneNumber,
        organizationLocationId: formData.organizationLocationId,
        useCustomAddress: formData.useCustomAddress,
        pointOfContactPersons: formData.pointOfContactPersons,
      } as EventInfoScreenValues),
    [formData],
  )

  const registrationDetailsValues = useMemo(() => {
    const { groups, questionnaires, waivers, instructions, visibility } = formData
    return {
      questionnaires,
      waivers,
      instructions,
      visibility,
      onboardingGroups: groups.filter(
        ({ associationType }) => associationType === AssociationType.ADD_PARTICIPANTS_TO_GROUP,
      ),
    }
  }, [formData])

  const handleOpportunitySubmit = useCallback(
    (values: FormData) => {
      complete(mapFormDataToOpportunityPayload(values))
    },
    [complete],
  )

  const handleEventSubmit = useCallback(
    async (values: FormData, asDraft: boolean = false) => {
      let startDateChanged = false
      if (!isDraftable && values.recurringType !== RECURRING_TYPE.NEVER) {
        let editMode: EditType
        startDateChanged = !moment(values.startDate as Date).isSame(event!.startsAt, 'day')
        if (startDateChanged) {
          // we edit date of single event, so it's a single edit without prompt
          editMode = EditType.Single
        } else {
          const { editType } = await showEditRecurringPrompt()
          editMode = editType
        }
        if (editMode === 'single') {
          values.recurringType = RECURRING_TYPE.NEVER
        }
        values.editMode = editMode
      }
      complete(
        mapFormDataToEventPayload(
          values,
          isQuestionnaireAsSurveyEnabled,
          roles as unknown as Role[],
          currentOrg.timeZone,
          asDraft,
          isSimplifyRecurringEventRulesEnabled,
          startDateChanged,
        ),
      )
    },
    [
      complete,
      currentOrg.timeZone,
      event,
      isDraftable,
      isQuestionnaireAsSurveyEnabled,
      isSimplifyRecurringEventRulesEnabled,
      roles,
      showEditRecurringPrompt,
    ],
  )

  const handleSubmit = useCallback(
    (values: Partial<FormData>, asDraft: boolean = false) => {
      const data = {
        ...formData,
        ...values,
      }
      if (data.isSchedulable) {
        return handleEventSubmit(data, asDraft)
      } else {
        return handleOpportunitySubmit(data)
      }
    },
    [formData, handleEventSubmit, handleOpportunitySubmit],
  )

  const handleRegistrationsScreenSubmit = useCallback(
    ({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues, asDraft: boolean = false) => {
      const data = {
        ...formData,
        ...values,
        groups: [
          ...formData.groups.filter(
            ({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP,
          ),
          ...onboardingGroups,
        ],
      }
      return handleSubmit(data, asDraft)
    },
    [formData, handleSubmit],
  )

  const handleDateSubmit = useCallback(
    async (values: EventDateScreenValues, asDraft: boolean = false) => {
      let opportunityTemplateGroups: OpportunityTemplatePersonGroup[] = []
      let opportunityTemplateSurveys: OpportunityTemplateSurvey[] = []
      let opportunityTemplateCredentials: OpportunityTemplateCredential[] = []
      let fromTemplate: Record<string, any> = {}
      if (
        values.opportunityTemplate &&
        values.useTemplate &&
        formData.opportunityTemplate?.id !== values.opportunityTemplate.id
      ) {
        ;[opportunityTemplateGroups, opportunityTemplateSurveys, opportunityTemplateCredentials] = await Promise.all([
          fetchOpportunityTemplateGroups(values.opportunityTemplate.id),
          fetchOpportunityTemplateSurveys(values.opportunityTemplate.id),
          fetchOpportunityTemplateCredentials(values.opportunityTemplate.id),
        ])
        fromTemplate = getFormValuesFromOpportunityTemplate({
          opportunityTemplateGroups,
          opportunityTemplate: values.opportunityTemplate,
          opportunityTemplateSurveys,
          opportunityTemplateCredentials,
          namedLocations,
        })
      }
      setFormData(data => ({
        ...data,
        ...values,
        ...fromTemplate,
      }))
      if (asDraft) {
        handleSubmit({}, true)
      } else {
        setScreen(1)
      }
    },
    [
      fetchOpportunityTemplateCredentials,
      fetchOpportunityTemplateGroups,
      fetchOpportunityTemplateSurveys,
      formData.opportunityTemplate,
      handleSubmit,
      namedLocations,
    ],
  )

  const handleInfoSubmit = useCallback(
    ({ visibilityGroups, ...values }: EventInfoScreenValues, asDraft: boolean = false) => {
      setFormData(({ groups, ...data }) => ({
        ...data,
        ...values,
        groups: [
          ...groups.filter(({ associationType }) => associationType !== AssociationType.EVENT_PRIVATE_TO_MEMBERS),
          ...visibilityGroups,
        ],
      }))
      if (asDraft) {
        handleSubmit({}, true)
      } else {
        setScreen(2)
      }
    },
    [handleSubmit],
  )

  const handlePreviousScreen = useCallback((values: Partial<FormData>) => {
    setFormData(data => ({
      ...data,
      ...values,
    }))
    setScreen(value => value - 1)
  }, [])

  const handleRegistrationsScreenBack = useCallback(
    ({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues) => {
      return handlePreviousScreen({
        ...values,
        groups: [
          ...formData.groups.filter(
            ({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP,
          ),
          ...onboardingGroups,
        ],
      })
    },
    [formData.groups, handlePreviousScreen],
  )

  const handleOverviewBack = useCallback(
    (values: EventInfoScreenValues) => {
      return handlePreviousScreen(values)
    },
    [handlePreviousScreen],
  )

  if (
    groupsLoading ||
    eventGroupsLoading ||
    isTesterLoading ||
    !rolesLoaded ||
    questionSetsLoading ||
    locationsLoading
  ) {
    return <Loading />
  }

  return (
    <FormModal id="trip-prompt" showing={showing} close={close} title={titles[screen]}>
      {
        [
          <EventDateAndRoleScreen
            values={dateValues}
            onBack={close}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleDateSubmit}
            roles={roles as Role[]}
            addRole={addRole as () => Promise<Role>}
          />,
          <EventInfoScreen
            values={eventInformationValues}
            onBack={handleOverviewBack}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleInfoSubmit}
            groups={groups}
            isSchedulable={formData.isSchedulable}
            namedLocations={namedLocations}
          />,
          <EventRegistrationDetailsScreen
            values={registrationDetailsValues}
            onBack={handleRegistrationsScreenBack}
            isNewEvent={isNewEvent}
            isDraftable={isDraftable}
            onSubmit={handleRegistrationsScreenSubmit}
            groups={groups}
            isSchedulable={formData.isSchedulable}
          />,
        ][screen]
      }
    </FormModal>
  )
}

export default AddEditEventPrompt
